import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  FileCheck,
  Clock,
  XCircle,
  Eye,
  Download,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  RotateCw,
  Search,
  PlusCircle,
  FileSignature,
  Activity,
} from 'lucide-react';
import { documentApi } from '../services/api';
import { useAuth } from '../stores/authStore';
import { toast } from '../stores/feedbackStore';
import { formatRelativeTime } from '@pdfsign/shared/utils';
import { Document, DocumentStatus } from '@pdfsign/shared/types';

const statusConfig: Record<DocumentStatus, { label: string; badgeClass: string; icon: React.ReactNode }> = {
  draft: { label: 'Bản nháp', badgeClass: 'badge-gray', icon: <FileText className="w-3 h-3" /> },
  pending_sign: { label: 'Chờ ký', badgeClass: 'badge-warning', icon: <Clock className="w-3 h-3" /> },
  in_progress: { label: 'Đang ký', badgeClass: 'badge-primary', icon: <Clock className="w-3 h-3" /> },
  completed: { label: 'Hoàn tất', badgeClass: 'badge-success', icon: <FileCheck className="w-3 h-3" /> },
  rejected: { label: 'Từ chối', badgeClass: 'badge-danger', icon: <XCircle className="w-3 h-3" /> },
  expired: { label: 'Hết hạn', badgeClass: 'badge-gray', icon: <Clock className="w-3 h-3" /> },
  cancelled: { label: 'Đã hủy', badgeClass: 'badge-gray', icon: <XCircle className="w-3 h-3" /> },
};

const typeLabels: Record<string, string> = {
  bao_cao: 'Báo cáo',
  quyet_dinh: 'Quyết định',
  to_trinh: 'Tờ trình',
  cong_van: 'Công văn',
  hop_dong: 'Hợp đồng',
  khac: 'Khác',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const fetchDocuments = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await documentApi.list({
        page: 1,
        limit: 50,
        sort_by: 'updated_at',
        sort_order: 'desc',
      });
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleDownload = async (id: string, fileName: string) => {
    try {
      const response = await documentApi.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Đang tải xuống tệp: ${fileName}`);
    } catch (error) {
      toast.error('Không thể tải xuống tài liệu');
    }
  };

  // Metrics
  const totalCount = documents.length;
  const inProgressCount = documents.filter((d) => d.status === 'in_progress').length;
  const pendingCount = documents.filter((d) => ['draft', 'pending_sign'].includes(d.status)).length;
  const completedCount = documents.filter((d) => d.status === 'completed').length;

  // Filtered documents
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      if (filterStatus === 'in_progress' && doc.status !== 'in_progress') return false;
      if (filterStatus === 'pending' && !['draft', 'pending_sign'].includes(doc.status)) return false;
      if (filterStatus === 'completed' && doc.status !== 'completed') return false;
      if (filterType !== 'all' && doc.type !== filterType) return false;

      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchTitle = doc.title.toLowerCase().includes(q);
        const matchFileName = (doc.file_name || '').toLowerCase().includes(q);
        const matchType = (typeLabels[doc.type] || doc.type).toLowerCase().includes(q);
        return matchTitle || matchFileName || matchType;
      }

      return true;
    });
  }, [documents, filterStatus, filterType, search]);

  return (
    <div className="space-y-4 animate-fade-in w-full">
      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200/80 dark:border-white/10">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Trung tâm điều hành ký số
            </h1>
            <span className="badge badge-primary text-[10px] font-mono py-0 px-2 font-normal">Executive</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-normal">
            Xin chào <span className="font-medium text-slate-800 dark:text-slate-200">{user?.full_name || 'Quản trị viên'}</span> · Giám sát & phê duyệt tài liệu điện tử theo chuẩn Nghị định 30/2020/NĐ-CP
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => fetchDocuments(true)}
            disabled={refreshing}
            className="btn-outline text-xs inline-flex items-center gap-1.5 h-8 px-2.5 font-normal"
            title="Đồng bộ lại dữ liệu"
          >
            <RotateCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-sky-500' : ''}`} />
            <span>Làm mới</span>
          </button>
          <Link
            to="/documents/create"
            className="btn-primary text-xs inline-flex items-center gap-1.5 h-8 px-3 shadow-sm shadow-sky-500/20 font-medium"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            <span>Tạo tài liệu mới</span>
          </Link>
        </div>
      </div>

      {/* High-density Compact KPI Cards (Horizontal layout, low profile) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card px-3.5 py-2.5 flex items-center gap-3 hover:border-slate-300 dark:hover:border-white/20 transition-all">
          <div className="w-8 h-8 rounded-lg bg-sky-500/10 text-sky-500 dark:text-sky-400 flex items-center justify-center flex-shrink-0 border border-sky-500/20">
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="text-xs font-normal text-slate-500 dark:text-slate-400 block truncate">
              Tổng văn bản
            </span>
            <span className="text-lg sm:text-xl font-semibold font-mono text-slate-900 dark:text-white leading-tight">
              {totalCount}
            </span>
          </div>
        </div>

        <div className="card px-3.5 py-2.5 flex items-center gap-3 hover:border-amber-500/40 transition-all">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 dark:text-amber-400 flex items-center justify-center flex-shrink-0 border border-amber-500/20">
            <Clock className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="text-xs font-normal text-amber-600 dark:text-amber-400 block truncate">
              Đang trong luồng ký
            </span>
            <span className="text-lg sm:text-xl font-semibold font-mono text-slate-900 dark:text-white leading-tight">
              {inProgressCount}
            </span>
          </div>
        </div>

        <div className="card px-3.5 py-2.5 flex items-center gap-3 hover:border-indigo-500/40 transition-all">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 flex items-center justify-center flex-shrink-0 border border-indigo-500/20">
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="text-xs font-normal text-indigo-600 dark:text-indigo-400 block truncate">
              Cần xử lý / Chờ ký
            </span>
            <span className="text-lg sm:text-xl font-semibold font-mono text-slate-900 dark:text-white leading-tight">
              {pendingCount}
            </span>
          </div>
        </div>

        <div className="card px-3.5 py-2.5 flex items-center gap-3 hover:border-emerald-500/40 transition-all">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 border border-emerald-500/20">
            <FileCheck className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="text-xs font-normal text-emerald-600 dark:text-emerald-400 block truncate">
              Đã ký hoàn tất
            </span>
            <span className="text-lg sm:text-xl font-semibold font-mono text-slate-900 dark:text-white leading-tight">
              {completedCount}
            </span>
          </div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="card p-2.5 sm:p-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tiêu đề, số hiệu, tên tệp..."
              className="input-executive pl-8 text-xs h-8 w-full rounded-lg"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                ✕
              </button>
            )}
          </div>

          {/* Filter Status Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                filterStatus === 'all'
                  ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/30'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
              }`}
            >
              Tất cả ({totalCount})
            </button>
            <button
              onClick={() => setFilterStatus('in_progress')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                filterStatus === 'in_progress'
                  ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
              }`}
            >
              Đang ký ({inProgressCount})
            </button>
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                filterStatus === 'pending'
                  ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
              }`}
            >
              Chờ xử lý ({pendingCount})
            </button>
            <button
              onClick={() => setFilterStatus('completed')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                filterStatus === 'completed'
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
              }`}
            >
              Đã hoàn tất ({completedCount})
            </button>

            {/* Document Type Dropdown */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input-executive text-xs h-7 py-0 px-2 rounded-lg ml-1 font-normal text-slate-300"
            >
              <option value="all">Tất cả loại</option>
              {Object.entries(typeLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Full-Width Document Feed */}
      <div className="card overflow-hidden">
        <div className="py-2.5 px-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
            <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              Danh sách hồ sơ ký số ({filteredDocuments.length})
            </h2>
          </div>
          <Link
            to="/documents"
            className="text-xs font-medium text-sky-600 dark:text-sky-400 hover:underline inline-flex items-center gap-1"
          >
            <span>Sổ văn bản chi tiết</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            <RotateCw className="w-5 h-5 animate-spin mx-auto text-sky-500 mb-2" />
            Đang tải dữ liệu hồ sơ ký số...
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {search || filterStatus !== 'all' || filterType !== 'all'
                ? 'Không tìm thấy hồ sơ nào khớp với bộ lọc'
                : 'Chưa có văn bản nào trong hệ thống'}
            </p>
            <p className="text-[11px] text-slate-400 mb-3">
              Bắt đầu tạo hồ sơ mới hoặc tải tệp tài liệu PDF để khởi tạo luồng ký số.
            </p>
            <Link to="/documents/create" className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 font-medium">
              <PlusCircle className="w-3.5 h-3.5" />
              <span>Tạo tài liệu mới</span>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-white/5">
            {filteredDocuments.map((doc) => {
              const cfg = statusConfig[doc.status as DocumentStatus] || statusConfig.draft;
              const isWaitingSign = ['in_progress', 'pending_sign'].includes(doc.status);

              return (
                <div
                  key={doc.id}
                  className="py-2.5 px-3.5 sm:px-4 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-3"
                >
                  {/* Left: Doc Icon + Info */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200/50 dark:border-sky-500/20 flex items-center justify-center flex-shrink-0 text-sky-600 dark:text-sky-400">
                      <FileSignature className="w-4 h-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/documents/${doc.id}`}
                          className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 hover:text-sky-600 dark:hover:text-sky-400 transition-colors truncate"
                        >
                          {doc.title}
                        </Link>
                        <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-normal border border-slate-200 dark:border-white/10">
                          {typeLabels[doc.type] || doc.type}
                        </span>
                      </div>

                      <p className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap font-mono mt-0.5 font-normal">
                        <span>Tệp: {doc.file_name}</span>
                        <span>·</span>
                        <span>Cập nhật: {formatRelativeTime(doc.updated_at)}</span>
                        {doc.pages_count > 0 && (
                          <>
                            <span>·</span>
                            <span>{doc.pages_count} trang</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Right: Stepper + Status Badge + Actions */}
                  <div className="flex items-center justify-between md:justify-end gap-3 flex-shrink-0">
                    {/* Stepper info */}
                    <div className="flex flex-col items-start md:items-end gap-0.5">
                      <span className="text-[10px] font-mono font-normal text-slate-400">
                        Tiến độ: <span className="font-medium text-slate-700 dark:text-slate-200">{doc.current_step}</span>/{doc.total_steps}
                      </span>
                      <div className="w-20 bg-slate-200 dark:bg-slate-800 rounded-full h-1 overflow-hidden">
                        <div
                          className={`h-1 rounded-full transition-all ${
                            doc.status === 'completed' ? 'bg-emerald-500' : 'bg-sky-500'
                          }`}
                          style={{
                            width: `${doc.total_steps > 0 ? (doc.current_step / doc.total_steps) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span className={cfg.badgeClass}>
                      {cfg.icon}
                      <span className="text-[11px] font-normal">{cfg.label}</span>
                    </span>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                      {isWaitingSign && (
                        <Link
                          to={`/documents/${doc.id}`}
                          className="btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1 font-medium h-7"
                        >
                          <FileSignature className="w-3 h-3" />
                          <span>Ký ngay</span>
                        </Link>
                      )}

                      <Link
                        to={`/documents/${doc.id}`}
                        className="p-1.5 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        title="Xem chi tiết"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Link>

                      {doc.status === 'completed' && (
                        <button
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors"
                          title="Tải văn bản đã ký"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compact Telemetry Footer */}
      <div className="py-2 px-3.5 rounded-xl bg-slate-900/80 dark:bg-[#111827] border border-slate-800 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-2 text-slate-300">
          <ShieldCheck className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
          <span>Mã hóa SHA-256 & Lưu trữ phân tán Cloudflare Edge</span>
        </div>

        <div className="flex items-center gap-3 text-slate-400 font-mono flex-shrink-0 text-[10px]">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" /> D1 Database Online
          </span>
          <span className="flex items-center gap-1">
            <Activity className="w-3 h-3 text-sky-400" /> Worker API (:8787)
          </span>
        </div>
      </div>
    </div>
  );
}